import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Clock, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { cn, apiRequest } from '../lib/utils';
import ChatWidget from './ChatWidget';
import AppointmentModal from './AppointmentModal';

// Company branding
const COMPANY = {
  name: 'Community Dental Lab, PC',
  tagline: 'Oregon Coast',
  logoLight: 'https://customer-assets.emergentagent.com/job_2837485d-5657-4508-82a6-ac79384a460f/artifacts/mauh7fgy_5.jpg',
  logoDark: 'https://customer-assets.emergentagent.com/job_2837485d-5657-4508-82a6-ac79384a460f/artifacts/oj0ocik8_4.jpg',
  logoOrange: 'https://customer-assets.emergentagent.com/job_2837485d-5657-4508-82a6-ac79384a460f/artifacts/emqjdrie_6.jpg',
};

// Helper to format time from 24hr to 12hr
const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Helper to get business hours summary for header
const getBusinessHoursSummary = (businessHours) => {
  if (!businessHours) return 'Call for hours';
  
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const openDays = days.filter(day => businessHours[day]?.is_open);
  
  if (openDays.length === 0) return 'Closed';
  
  // Find the most common hours
  const hourCounts = {};
  openDays.forEach(day => {
    const hours = businessHours[day];
    const key = `${hours.open_time}-${hours.close_time}`;
    hourCounts[key] = (hourCounts[key] || 0) + 1;
  });
  
  const mostCommon = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0];
  const [openTime, closeTime] = mostCommon[0].split('-');
  
  // Format days string
  let daysStr = '';
  if (openDays.length === 5 && openDays.every(d => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(d))) {
    daysStr = 'Mon-Fri';
  } else if (openDays.length === 4) {
    const closedDay = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].find(d => !openDays.includes(d));
    if (closedDay) {
      daysStr = `Mon-Fri (${closedDay.slice(0, 3)} closed)`;
    } else {
      daysStr = openDays.map(d => d.slice(0, 3)).join(', ');
    }
  } else {
    daysStr = openDays.map(d => d.slice(0, 3)).join(', ');
  }
  
  return `${daysStr}: ${formatTime12hr(openTime)} - ${formatTime12hr(closeTime)}`;
};

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { 
    label: 'Services',
    children: [
      { href: '/services', label: 'All Services' },
      { href: '/mouth-guards', label: 'Mouth Guards' },
    ]
  },
  { href: '/gallery', label: 'Before & After' },
  { href: '/cost-comparison', label: 'Cost Comparison' },
  { href: '/insurance-payment', label: 'Insurance & Payment' },
  { href: '/blog', label: 'Blog' },
  { href: '/faq', label: 'FAQ' },
  { href: '/contact', label: 'Contact' },
];

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [businessHours, setBusinessHours] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Fetch business hours from settings
    const fetchHours = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/business-hours`);
        if (response.ok) {
          const data = await response.json();
          setBusinessHours(data.business_hours);
        }
      } catch (error) {
        console.log('Could not fetch business hours');
      }
    };
    fetchHours();
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      )}
      data-testid="main-navigation"
    >
      {/* Top bar */}
      <div className={cn(
        'hidden lg:block border-b transition-all duration-300',
        isScrolled ? 'border-slate-100 py-2' : 'border-transparent py-3 bg-slate-900'
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={cn(
            'flex items-center justify-between text-sm',
            isScrolled ? 'text-slate-600' : 'text-slate-300'
          )}>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>(541) 756-2121</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>North Bend, Oregon</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{businessHours ? getBusinessHoursSummary(businessHours) : 'Call for hours'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Community Dental Lab
              </span>
              <span className="text-xs text-orange-600 font-medium tracking-wide">
                OREGON COAST
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((item, idx) => (
              item.children ? (
                <div
                  key={idx}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(idx)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    className={cn(
                      'flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg',
                      isScrolled ? 'text-slate-700 hover:text-orange-600 hover:bg-orange-50' : 'text-slate-700 hover:text-orange-600'
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {openDropdown === idx && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-2 animate-fade-in">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-slate-700 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                          data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'px-3 py-2 text-sm font-medium transition-colors rounded-lg',
                    location.pathname === item.href
                      ? 'text-orange-600'
                      : isScrolled ? 'text-slate-700 hover:text-orange-600 hover:bg-orange-50' : 'text-slate-700 hover:text-orange-600'
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </div>

          {/* CTA + Mobile Menu */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowAppointment(true)}
              className="hidden sm:flex bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 shadow-lg shadow-orange-600/20 transition-transform hover:scale-105"
              data-testid="schedule-consultation-btn"
            >
              Schedule Consultation
            </Button>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b">
                    <div className="flex flex-col">
                      <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Community Dental Lab
                      </span>
                      <span className="text-xs text-orange-600 font-medium tracking-wide">
                        OREGON COAST
                      </span>
                    </div>
                  </div>
                  <nav className="flex-1 overflow-y-auto p-4">
                    {navLinks.map((item, idx) => (
                      item.children ? (
                        <div key={idx} className="mb-2">
                          <p className="px-4 py-2 text-sm font-medium text-slate-500">{item.label}</p>
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="block px-4 py-3 text-slate-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              data-testid={`mobile-nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={cn(
                            'block px-4 py-3 rounded-lg transition-colors mb-1',
                            location.pathname === item.href
                              ? 'text-orange-600 bg-orange-50'
                              : 'text-slate-700 hover:text-orange-600 hover:bg-orange-50'
                          )}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {item.label}
                        </Link>
                      )
                    ))}
                  </nav>
                  <div className="p-4 border-t">
                    <Button 
                      onClick={() => {
                        setMobileOpen(false);
                        setShowAppointment(true);
                      }}
                      className="w-full bg-orange-600 hover:bg-orange-700 rounded-full"
                    >
                      Schedule Consultation
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
      
      {/* Appointment Modal */}
      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </header>
  );
}

export function Footer() {
  const [businessHours, setBusinessHours] = useState(null);
  
  useEffect(() => {
    const fetchHours = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/business-hours`);
        if (response.ok) {
          const data = await response.json();
          setBusinessHours(data.business_hours);
        }
      } catch (error) {
        console.log('Could not fetch business hours');
      }
    };
    fetchHours();
  }, []);
  
  // Format hours for footer display
  const getFooterHours = () => {
    if (!businessHours) return 'Call for hours';
    
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const openDays = days.filter(day => businessHours[day]?.is_open);
    
    if (openDays.length === 0) return 'Closed';
    
    // Group days by hours
    const groups = {};
    openDays.forEach(day => {
      const hours = businessHours[day];
      const key = `${hours.open_time}-${hours.close_time}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(day);
    });
    
    return Object.entries(groups).map(([timeRange, groupDays]) => {
      const [open, close] = timeRange.split('-');
      const daysStr = groupDays.map(d => d.slice(0, 3)).join(', ');
      return `${daysStr}: ${formatTime12hr(open)} - ${formatTime12hr(close)}`;
    }).join(' | ');
  };
  
  return (
    <footer className="bg-slate-900 text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={COMPANY.logoDark} 
                alt={COMPANY.name}
                className="h-36 w-auto object-contain"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Oregon's longest standing Denturist practice. Direct care, personalized attention, advanced materials.
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Services</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li><Link to="/services" className="hover:text-orange-500 transition-colors">Full Dentures</Link></li>
              <li><Link to="/services" className="hover:text-orange-500 transition-colors">Partial Dentures</Link></li>
              <li><Link to="/services" className="hover:text-orange-500 transition-colors">Denture Repairs</Link></li>
              <li><Link to="/mouth-guards" className="hover:text-orange-500 transition-colors">Mouth Guards</Link></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li><Link to="/about" className="hover:text-orange-500 transition-colors">About Us</Link></li>
              <li><Link to="/gallery" className="hover:text-orange-500 transition-colors">Before & After</Link></li>
              <li><Link to="/cost-comparison" className="hover:text-orange-500 transition-colors">Cost Comparison</Link></li>
              <li><Link to="/faq" className="hover:text-orange-500 transition-colors">FAQ</Link></li>
              <li><Link to="/blog" className="hover:text-orange-500 transition-colors">Blog</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact</h4>
            <ul className="space-y-4 text-sm text-slate-400">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 mt-0.5 text-orange-500" />
                <span>(541) 756-2121</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 text-orange-500" />
                <span>2495 Newmark St<br />North Bend, OR 97459</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 mt-0.5 text-orange-500" />
                <span>{businessHours ? getFooterHours() : 'Call for hours'}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Areas Served */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <h4 className="font-medium text-sm text-slate-300 mb-4">Proudly Serving</h4>
          <p className="text-sm text-slate-500">
            Coos Bay • North Bend • Bandon • Reedsport • Coquille • Myrtle Point • Brookings • Florence • Roseburg • Gold Beach • Lakeside • Charleston • Winchester Bay
          </p>
        </div>

        {/* Bottom */}
        <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-orange-500 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-orange-500 transition-colors">Terms of Service</Link>
            <Link to="/admin" className="hover:text-orange-500 transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1">
        {children || <Outlet />}
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
